﻿import {ErrorBundle, errorToErrorBundle, FormattableString, isNullOrEmpty} from "@2d-front-monorepo/core";
import {JSX, useEffect, useMemo, useRef} from "react";
import {getParentScrollable} from "../helpers/BoxModel";
import {scrollIntoView} from "../helpers/scrollIntoView";
import Message from "../HtmlControls/Message";
import {useMvvmContext} from "./MvvmDataProvider";
import {ErrorEntry} from "./MvvmContextState";

export interface MvvmErrorCollectorProps {
	title: FormattableString;
	debug?: boolean;
	captureErrors?: boolean;
}

function addErrors(errorBundle: ErrorBundle, errors: ErrorEntry[]): void {
	for (const error of errors) {
		errorBundle.errors.push({
			text: error.error,
			args: {value: error.value}
		});
	}
}

export default function MvvmErrorCollector(props: MvvmErrorCollectorProps): JSX.Element | undefined {
	const {debug, captureErrors, title} = props;

	const messageRef = useRef<HTMLDivElement>(null);
	const context = useMvvmContext<any>();

	const exception = context.hasExceptions() ? context.getExceptions()[0] : undefined;
	// getErrors() keeps a stable array identity until the error set actually changes,
	// so the memo (and the scroll effect keyed on the bundle) only re-fire on real changes.
	const errors = captureErrors && context.hasErrors() ? context.getErrors() : undefined;

	const errorBundle = useMemo((): ErrorBundle | undefined => {
		let bundle: ErrorBundle | undefined = undefined;
		if (exception) {
			bundle = errorToErrorBundle(exception, debug);
			if (isNullOrEmpty(bundle.detail?.text)) {
				bundle.detail = bundle.title;
			}
		}

		if (errors != null) {
			bundle ??= errorToErrorBundle(new Error());
			addErrors(bundle, errors);
		}

		return bundle;
	}, [exception, errors, debug]);

	useEffect(() => {
		const raf = requestAnimationFrame(() => {
			if (!errorBundle || !messageRef.current) {
				return;
			}

			const scrollable = getParentScrollable(messageRef.current);
			scrollIntoView(scrollable, messageRef.current, "top", "smooth");
		});

		return () => cancelAnimationFrame(raf);
	}, [errorBundle]);

	if (!errorBundle) {
		return undefined;
	}

	return <Message title={title}
	                detail={errorBundle.detail}
	                items={errorBundle.errors}
	                footer={errorBundle.footer}
	                type="error"
	                ref={messageRef}/>;
}
